import { useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';

import { ApiError, api } from '../api/client';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { SlipImageError, decodeSlipQr, readSlipImage } from '../lib/slipUpload';
import { Modal } from './Modal';

/**
 * LLMR-121: upload a bank slip against a top-up that shows expired/failed
 * despite having actually been paid. The QR on the slip is decoded here, in
 * the browser (lib/slipUpload.ts) — payment-gateway's verify call takes that
 * text, never the image — but the image uploads regardless of whether the
 * decode succeeds, since a human reviewing it later needs the photo either
 * way.
 *
 * Adds no credit itself. onDone only reports that the upload attempt
 * finished (and how) — the actual review happens in llmr-admin-web, on
 * staff's own schedule.
 */
export function SlipUploadModal({
  topUpId,
  onClose,
  onDone,
}: {
  topUpId: string;
  onClose: () => void;
  onDone: (result: 'submitted' | 'error') => void;
}) {
  const { dict } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [qrPayload, setQrPayload] = useState<string | null>(null);
  const [decoding, setDecoding] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  async function onPickFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setFileError(null);
    setSubmitError(null);
    setQrPayload(null);
    setImageDataUrl(null);

    try {
      const dataUrl = await readSlipImage(file);
      setImageDataUrl(dataUrl);
      setDecoding(true);
      try {
        setQrPayload(await decodeSlipQr(dataUrl));
      } finally {
        setDecoding(false);
      }
    } catch (err) {
      if (err instanceof SlipImageError) {
        setFileError(err.message === 'too_large' ? dict.topUp.uploadSlipTooLarge : dict.topUp.uploadSlipNotImage);
      } else {
        setFileError(dict.topUp.uploadSlipError);
      }
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!imageDataUrl || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.uploadTopUpSlip(topUpId, imageDataUrl, qrPayload ?? '');
      onClose();
      onDone('submitted');
    } catch (e) {
      setSubmitError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.topUp.uploadSlipError);
      setSubmitting(false);
    }
  }

  return (
    <Modal title={dict.topUp.uploadSlipModalTitle} onClose={submitting ? undefined : onClose}>
      <form onSubmit={(e) => void submit(e)}>
        <p className="note">{dict.topUp.uploadSlipInstructions}</p>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="visually-hidden"
          onChange={(e) => void onPickFile(e)}
        />

        {imageDataUrl ? (
          <div className="slip-upload-picker">
            <img src={imageDataUrl} alt="" className="slip-upload-preview" />
            <button type="button" className="ghost" onClick={() => fileInputRef.current?.click()}>
              {dict.topUp.uploadSlipChooseAnother}
            </button>
          </div>
        ) : (
          <button type="button" className="ghost btn-icon" onClick={() => fileInputRef.current?.click()}>
            {dict.topUp.uploadSlipChooseFile}
          </button>
        )}

        {fileError && (
          <p className="note warn-text" role="alert">
            {fileError}
          </p>
        )}

        {imageDataUrl && !decoding && (
          <p className="note">{qrPayload ? dict.topUp.uploadSlipQrFound : dict.topUp.uploadSlipQrNotFound}</p>
        )}

        {submitError && (
          <p className="note warn-text" role="alert">
            {submitError}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="ghost" onClick={onClose} disabled={submitting}>
            {dict.common.cancel}
          </button>
          <button type="submit" disabled={!imageDataUrl || decoding || submitting}>
            {submitting ? dict.topUp.uploadSlipSubmitting : dict.topUp.uploadSlipSubmit}
          </button>
        </div>
      </form>
    </Modal>
  );
}
